import { Dispatch, SetStateAction, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { UsersByRoleClassesRosterQuery } from '../../graphQLOperations/__generated__/UsersByRoleClassesRosterQuery.graphql';
import { usersByRoleClassesRoster } from '../../graphQLOperations/Users';
import { TeacherTableFilterState } from './TeachersDataTable';
import { CompleteUser } from '../../custom-hooks/useGetUsersBySite';
import { sortOrderCodeForQueries } from '../../businessLogicService/sortOrderCodeForQueries';

export interface QueryUsersDataProp {
  filterState: TeacherTableFilterState;
  setFetchingData: Dispatch<SetStateAction<boolean>>;
  setUsersFiltered: Dispatch<SetStateAction<CompleteUser[]>>;
  setTotalRecords: Dispatch<SetStateAction<number>>;
  setPageInfo: Dispatch<
    SetStateAction<
      | {
          endCursor: string | null | undefined;
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          startCursor: string | null | undefined;
        }
      | undefined
    >
  >;
  pageInfo:
    | {
        endCursor: string | null | undefined;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor: string | null | undefined;
      }
    | undefined;
  fetchingData: boolean;
  firstLoad: boolean;
  setFirstLoad: Dispatch<SetStateAction<boolean>>;
  teacherPageCursors?: Record<number, { endCursor?: string | null; startCursor?: string | null }>;
  setTeacherPageCursors?: Dispatch<
    SetStateAction<Record<number, { endCursor?: string | null; startCursor?: string | null }>>
  >;
}

export const QueryTeachersData = ({
  setFetchingData,
  setUsersFiltered,
  filterState,
  setTotalRecords,
  setPageInfo,
  pageInfo,
  fetchingData,
  firstLoad,
  setFirstLoad,
  teacherPageCursors,
  setTeacherPageCursors,
}: QueryUsersDataProp) => {
  const isMovingNext = filterState.page! - filterState.prevPage === 1;
  const isMovingPrevious = filterState.page! - filterState.prevPage === -1;
  const isNotMoving = filterState.page! - filterState.prevPage === 0;
  const currentPage = filterState.page ?? 0;
  const afterCursorForCurrentPage = currentPage > 0 ? teacherPageCursors?.[currentPage - 1]?.endCursor : undefined;
  const shouldUseAfter = fetchingData && (isMovingNext || (isNotMoving && currentPage > 0));

  const usersData = useLazyLoadQuery<UsersByRoleClassesRosterQuery>(
    usersByRoleClassesRoster,
    {
      filter: {
        userEmailContains: filterState.filters.emailAddress,
        userEmails: null,
        isActive: 'ACTIVE',
        siteIds: filterState.filters.sites?.map(st => st.id),
        classIds: filterState.filters.classes?.length ? filterState.filters.classes.map(cl => cl.id) : null,
        roleIds: filterState.filters.roles?.map(rl => rl.id).filter(id => id !== 'RoleNode:2'),
        userNameContains: filterState.filters.name,
      },
      first: fetchingData && (isMovingNext || isNotMoving || !pageInfo?.hasPreviousPage) ? filterState.rows : null,
      last: fetchingData && isMovingPrevious && pageInfo?.hasPreviousPage ? filterState.rows : null,
      order: [{ lastName: sortOrderCodeForQueries(filterState.sortOrder || 1) }],
      skip: !firstLoad && !fetchingData,
      after: shouldUseAfter ? afterCursorForCurrentPage : undefined,
      before: fetchingData && isMovingPrevious && pageInfo?.hasPreviousPage ? pageInfo?.startCursor : undefined,
    },
    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(filterState) },
  );

  useEffect(() => {
    if (usersData?.allUsers) {
      setTotalRecords(usersData?.allUsers?.totalCount ?? 0);
      setPageInfo(usersData?.allUsers?.pageInfo);
      setTeacherPageCursors?.(prev => ({
        ...prev,
        [currentPage]: {
          startCursor: usersData?.allUsers?.pageInfo?.startCursor,
          endCursor: usersData?.allUsers?.pageInfo?.endCursor,
        },
      }));
      if (setUsersFiltered) {
        setUsersFiltered(
          usersData?.allUsers?.nodes?.map(
            user =>
              ({
                emailAddress: user.email,
                role: user.roles,
                site: user.sites,
                id: user?.id ?? '',
                name: `${user?.firstName || ''} ${user?.lastName || ''}`,
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                publicUserId: user?.publicUserId || '',
                grades: user.gradeLevels,
                classes:
                  user?.userClasses
                    ?.filter(uc => uc?.isActive)
                    .map(uc => (uc?.class?.section ? `${uc?.class.name} - ${uc.class.section}` : uc?.class?.name))
                    ?.join(', ') ?? '',
                externalSync: !!user.externalSync?.source,
              }) as unknown as CompleteUser,
          ) ?? [],
        );
      }
      setFirstLoad(false);
    }
    setFetchingData(false);
  }, [usersData]);

  return <div style={{ display: 'none' }}></div>;
};
